import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Scissors, Baby, Calculator, Home, Zap, Monitor, Sparkles, Dumbbell, Smile,
  Building, ChevronRight, Check, Loader2, ArrowLeft, CreditCard, Users,
  HardDrive, Package
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATE_ICONS = {
  scissors: Scissors, baby: Baby, calculator: Calculator, home: Home,
  zap: Zap, monitor: Monitor, sparkles: Sparkles, dumbbell: Dumbbell,
  smile: Smile, building: Building
};

export default function ClientWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [templates, setTemplates] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');

  const [formData, setFormData] = useState({
    template_slug: '',
    business_name: '',
    slug: '',
    owner_email: '',
    plan_slug: 'starter'
  });
  const [onboardingResult, setOnboardingResult] = useState(null);

  useEffect(() => {
    Promise.all([fetchTemplates(), fetchPlans()]);
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates?status=active`);
      setTemplates(response.data);
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  };

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/billing/plans`);
      setPlans(response.data);
    } catch { console.error('Failed to load plans'); }
  };

  const handleTemplateSelect = (slug) => {
    setFormData(prev => ({ ...prev, template_slug: slug }));
    setStep(2);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'business_name') {
      const slug = value.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handlePlanSelect = (slug) => {
    setFormData(prev => ({ ...prev, plan_slug: slug }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.business_name.trim()) { toast.error('Please enter a business name'); return; }
    setStep(3);
  };

  const handleCheckout = async () => {
    setCreating(true);
    try {
      const response = await axios.post(`${API}/billing/checkout`, {
        plan_slug: formData.plan_slug,
        billing_cycle: billingCycle,
        origin_url: window.location.origin,
        tenant_name: formData.business_name,
        tenant_slug: formData.slug,
        template_slug: formData.template_slug,
        owner_email: formData.owner_email
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setCreating(false);
    }
  };

  const handleFreeTrial = async () => {
    if (!formData.owner_email) { toast.error('Please enter your email to receive login credentials'); setStep(2); return; }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/tenants`, {
        name: formData.business_name,
        slug: formData.slug,
        template_slug: formData.template_slug,
        owner_email: formData.owner_email
      });
      const onboarding = response.data.onboarding || {};
      setOnboardingResult({
        tenantName: formData.business_name,
        slug: response.data.slug,
        templateSlug: formData.template_slug,
        adminEmail: onboarding.admin_email || formData.owner_email,
        tempPassword: onboarding.temp_password || '',
        crmUrl: `/${response.data.slug}/dashboard`,
        parentPortalUrl: `/${response.data.slug}/parent`,
        loginUrl: `/${response.data.slug}/login`,
        tenantId: response.data.id,
      });
      localStorage.setItem('tenant_id', response.data.id);
      localStorage.setItem('tenant_slug', response.data.slug);
      setStep(4);
      toast.success('Your CRM is ready!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create business');
    } finally { setCreating(false); }
  };

  const selectedTemplate = templates.find(t => t.slug === formData.template_slug);
  const selectedPlan = plans.find(p => p.slug === formData.plan_slug);
  const price = selectedPlan ? (billingCycle === 'monthly' ? selectedPlan.monthly_price : selectedPlan.annual_price) : 0;

  if (loading) {
    return (
      <div className="wizard-container">
        <div className="wizard-loading"><Loader2 className="animate-spin" size={40} /><p>Loading templates...</p></div>
      </div>
    );
  }

  return (
    <div className="wizard-container" data-testid="client-wizard">
      <div className="wizard-header">
        <h1 className="wizard-title">Create Your Business</h1>
        <p className="wizard-subtitle">Select your industry, configure your business, and choose a plan</p>
        <div className="wizard-progress">
          {['Select Industry', 'Business Details', 'Choose Plan', 'Complete'].map((label, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div className="progress-line" />}
              <div className={`progress-step ${step >= i + 1 ? 'active' : ''} ${step > i + 1 ? 'completed' : ''}`}>
                <div className="step-number">{step > i + 1 ? <Check size={16} /> : i + 1}</div>
                <span>{label}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="wizard-content">
        {/* Step 1: Select Industry */}
        {step === 1 && (
          <div className="template-grid" data-testid="template-selection">
            {templates.map(template => {
              const IconComponent = TEMPLATE_ICONS[template.icon] || Building;
              return (
                <div key={template.slug} className={`template-card ${formData.template_slug === template.slug ? 'selected' : ''}`}
                  onClick={() => handleTemplateSelect(template.slug)} data-testid={`template-${template.slug}`}>
                  <div className="template-icon" style={{ background: template.default_theme?.primary_color || '#8B7355' }}>
                    <IconComponent size={32} />
                  </div>
                  <h3 className="template-name">{template.name}</h3>
                  <p className="template-description">{template.description}</p>
                  <div className="template-modules">
                    {template.modules_enabled?.slice(0, 4).map(mod => (
                      <span key={mod} className="module-tag">{mod.replace('_', ' ')}</span>
                    ))}
                    {template.modules_enabled?.length > 4 && <span className="module-tag more">+{template.modules_enabled.length - 4}</span>}
                  </div>
                  <div className="template-select-btn">Select <ChevronRight size={16} /></div>
                </div>
              );
            })}
          </div>
        )}

        {/* Step 2: Business Details */}
        {step === 2 && (
          <div className="business-form-container">
            <button className="back-btn" onClick={() => setStep(1)} type="button"><ArrowLeft size={18} /> Back to Templates</button>
            {selectedTemplate && (
              <div className="selected-template-banner">
                <div className="banner-icon" style={{ background: selectedTemplate.default_theme?.primary_color || '#8B7355' }}>
                  {React.createElement(TEMPLATE_ICONS[selectedTemplate.icon] || Building, { size: 24 })}
                </div>
                <div className="banner-info">
                  <span className="banner-label">Selected Template</span>
                  <strong>{selectedTemplate.name}</strong>
                </div>
              </div>
            )}
            <form onSubmit={handleSubmit} className="business-form" data-testid="business-form">
              <div className="form-group">
                <label className="form-label">Business Name *</label>
                <input type="text" className="form-input" value={formData.business_name}
                  onChange={(e) => handleInputChange('business_name', e.target.value)}
                  placeholder="Enter your business name" required data-testid="business-name-input" />
              </div>
              <div className="form-group">
                <label className="form-label">Your Subdomain</label>
                <div className="slug-input-wrapper">
                  <input type="text" className="form-input slug-input" value={formData.slug}
                    onChange={(e) => handleInputChange('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    placeholder="your-business" data-testid="slug-input" />
                  <span className="slug-suffix">.depinacrm.com</span>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Owner Email (Optional)</label>
                <input type="email" className="form-input" value={formData.owner_email}
                  onChange={(e) => handleInputChange('owner_email', e.target.value)}
                  placeholder="owner@example.com" data-testid="email-input" />
              </div>
              <button type="submit" className="submit-btn" data-testid="continue-to-plan-btn">
                Continue to Plan Selection <ChevronRight size={20} />
              </button>
            </form>
          </div>
        )}

        {/* Step 3: Choose Plan & Pay */}
        {step === 3 && (
          <div className="plan-selection-container" data-testid="plan-selection">
            <button className="back-btn" onClick={() => setStep(2)} type="button"><ArrowLeft size={18} /> Back to Details</button>

            <div className="billing-toggle">
              <button className={`toggle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
                onClick={() => setBillingCycle('monthly')} data-testid="monthly-toggle">Monthly</button>
              <button className={`toggle-btn ${billingCycle === 'annual' ? 'active' : ''}`}
                onClick={() => setBillingCycle('annual')} data-testid="annual-toggle">
                Annual <span className="save-badge">Save 17%</span>
              </button>
            </div>

            <div className="plans-grid-wizard">
              {plans.map(plan => {
                const price = billingCycle === 'monthly' ? plan.monthly_price : plan.annual_price;
                const isSelected = formData.plan_slug === plan.slug;
                return (
                  <div key={plan.slug} className={`plan-card-wizard ${isSelected ? 'selected' : ''} ${plan.slug === 'professional' ? 'popular' : ''}`}
                    onClick={() => handlePlanSelect(plan.slug)} data-testid={`plan-${plan.slug}`}>
                    {plan.slug === 'professional' && <div className="popular-badge">Most Popular</div>}
                    <h3>{plan.name}</h3>
                    <div className="plan-price">
                      <span className="price-amount">${price}</span>
                      <span className="price-period">/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>
                    </div>
                    <ul className="plan-features-list">
                      <li><Users size={14} /> Up to {plan.user_limit} users</li>
                      <li><Zap size={14} /> {plan.automation_limit} automations</li>
                      <li><HardDrive size={14} /> {plan.storage_limit_mb >= 1000 ? `${plan.storage_limit_mb / 1000}GB` : `${plan.storage_limit_mb}MB`} storage</li>
                      <li><Package size={14} /> {plan.modules.length} modules</li>
                    </ul>
                    <div className="plan-modules">
                      {plan.modules.slice(0, 4).map(m => <span key={m} className="module-tag">{m.replace('_', ' ')}</span>)}
                      {plan.modules.length > 4 && <span className="module-tag more">+{plan.modules.length - 4}</span>}
                    </div>
                    {isSelected && <div className="plan-selected-indicator"><Check size={16} /> Selected</div>}
                  </div>
                );
              })}
            </div>

            <div className="checkout-summary">
              <div className="summary-row">
                <span>Template:</span>
                <strong>{selectedTemplate?.name}</strong>
              </div>
              <div className="summary-row">
                <span>Business:</span>
                <strong>{formData.business_name}</strong>
              </div>
              <div className="summary-row">
                <span>Plan:</span>
                <strong>{selectedPlan?.name} ({billingCycle})</strong>
              </div>
              <div className="summary-row total">
                <span>Total:</span>
                <strong>${price}/{billingCycle === 'monthly' ? 'mo' : 'yr'}</strong>
              </div>

              <div className="checkout-actions">
                <button className="checkout-btn" onClick={handleCheckout} disabled={creating} data-testid="checkout-btn">
                  {creating ? <><Loader2 className="animate-spin" size={18} /> Processing...</> : <><CreditCard size={18} /> Pay with Stripe</>}
                </button>
                <button className="trial-btn" onClick={handleFreeTrial} disabled={creating} data-testid="free-trial-btn">
                  Start 14-Day Free Trial
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Success / Onboarding Complete */}
        {step === 4 && onboardingResult && (
          <div className="onboarding-success" data-testid="onboarding-success">
            <div className="success-icon"><Check size={48} /></div>
            <h2>Your CRM is Ready!</h2>
            <p className="success-subtitle">{onboardingResult.tenantName} has been created successfully.</p>

            <div className="onboarding-credentials" data-testid="onboarding-credentials">
              <h3>Your Login Credentials</h3>
              <div className="cred-row"><span>Email</span><strong>{onboardingResult.adminEmail}</strong></div>
              <div className="cred-row"><span>Temporary Password</span><strong className="cred-password">{onboardingResult.tempPassword}</strong></div>
              <p className="cred-note">Please change your password on first login.</p>
            </div>

            <div className="onboarding-urls" data-testid="onboarding-urls">
              <h3>Your URLs</h3>
              <div className="url-row">
                <span>CRM Dashboard</span>
                <code>{onboardingResult.crmUrl}</code>
              </div>
              <div className="url-row">
                <span>Login Page</span>
                <code>{onboardingResult.loginUrl}</code>
              </div>
              {onboardingResult.templateSlug === 'daycare' && (
                <div className="url-row">
                  <span>Parent Portal</span>
                  <code>{onboardingResult.parentPortalUrl}</code>
                </div>
              )}
            </div>

            <div className="onboarding-note">
              <p>A welcome email has been sent to <strong>{onboardingResult.adminEmail}</strong> with these details.</p>
            </div>

            <div className="onboarding-actions">
              <button className="go-dashboard-btn" data-testid="go-to-dashboard-btn" onClick={async () => {
                try {
                  const res = await axios.post(`${API}/auth/login`, {
                    email: onboardingResult.adminEmail,
                    password: onboardingResult.tempPassword,
                    tenant_slug: onboardingResult.slug,
                  });
                  localStorage.setItem('auth_token', res.data.token);
                  localStorage.setItem('auth_user', JSON.stringify(res.data.user));
                  localStorage.setItem('tenant_id', res.data.user.tenant_id);
                  localStorage.setItem('tenant_slug', onboardingResult.slug);
                  navigate(`/${onboardingResult.slug}/dashboard`);
                } catch {
                  navigate(`/${onboardingResult.slug}/login`);
                }
              }}>
                Go to Dashboard <ChevronRight size={20} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
